import type { UserCompanyRoleSetViewDto } from '@/types/userRoles'

/**
 * A stable string standing for "what this user is allowed to do", for detecting a mid-session role
 * change by comparing a profile refresh against what we already held (ORB-698).
 *
 * Sorted at both levels because the server promises no ordering, and an array that merely came back
 * shuffled would otherwise read as somebody editing your access. `ETag` is deliberately excluded for
 * the same reason: it moves whenever the role-set document is written, including writes that leave
 * every capability exactly as it was.
 */
export function fingerprintRoleSets(roleSets: UserCompanyRoleSetViewDto[] | null | undefined): string {
  if (!roleSets) return ''

  const companies = roleSets
    .map(roleSet => {
      const workspaces = (roleSet.WorkspaceRoles ?? [])
        .map(workspace => [
          workspace.WorkspaceId,
          [...(workspace.Roles ?? [])].sort().join(','),
          workspace.CanAdmin ? 'a' : '',
          workspace.CanEdit ? 'e' : '',
          workspace.CanView ? 'v' : '',
        ].join(':'))
        .sort()
        .join('|')

      return [
        roleSet.ClientCompanyId,
        roleSet.IsAdmin ? 'admin' : '',
        [...(roleSet.CompanyRoles ?? [])].sort().join(','),
        workspaces,
      ].join('#')
    })
    .sort()

  return companies.join('||')
}
